import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import SourceAssignmentPicker, { normalizeSourceId } from '@/components/SourceAssignmentPicker';
import { organizationsService, type Organization } from '@/api/services/organizations';
import { workspaceSourcesService, workspaceSourcesToPickerSources } from '@/api/services/workspaceSources';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useMe } from '@/context/MeContext';
import { formatApiError } from '@/utils/formatApiError';
import { useToast } from '@/components/Toast';
import type { Source } from '@/types';

const WORKSPACE_DETAILS_PATH = '/workspace';

export default function OrganizationSettingsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { me } = useMe();
  const { workspaceVersion, isPersonalMode } = useWorkspace();
  const canManageMembers = me?.permissions.canManageWorkspaceMembers ?? false;
  const [org, setOrg] = useState<Organization | null>(null);
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [workspaceSources, setWorkspaceSources] = useState<Source[]>([]);
  const [enabledSourceIds, setEnabledSourceIds] = useState<Set<string>>(new Set());
  const [defaultMemberSourceIds, setDefaultMemberSourceIds] = useState<Set<string>>(new Set());
  const [sourcesLoadError, setSourcesLoadError] = useState<string | null>(null);
  const [savingSources, setSavingSources] = useState(false);
  const [sourcesSaveError, setSourcesSaveError] = useState<string | null>(null);

  const enabledSources = useMemo(
    () => workspaceSources.filter((source) => source.is_enabled),
    [workspaceSources],
  );

  const workspaceEnabledPickerSources = useMemo(
    () => enabledSources.filter((source) => enabledSourceIds.has(normalizeSourceId(source.id))),
    [enabledSourceIds, enabledSources],
  );

  useEffect(() => {
    if (isPersonalMode) {
      setOrg(null);
      setLoadError(null);
      return;
    }
    organizationsService
      .current()
      .then((o) => {
        setOrg(o);
        setName(o.name);
        setTimezone(o.timezone ?? 'UTC');
        setLoadError(null);
      })
      .catch((e) => setLoadError(formatApiError(e)));
  }, [isPersonalMode, workspaceVersion]);

  useEffect(() => {
    if (isPersonalMode || !canManageMembers) {
      setWorkspaceSources([]);
      setEnabledSourceIds(new Set());
      setDefaultMemberSourceIds(new Set());
      return;
    }
    workspaceSourcesService
      .getSettings()
      .then((settings) => {
        setWorkspaceSources(workspaceSourcesToPickerSources(settings.availableSources));
        setEnabledSourceIds(new Set(settings.enabledSourceIds.map(normalizeSourceId)));
        setDefaultMemberSourceIds(new Set(settings.defaultMemberSourceIds.map(normalizeSourceId)));
        setSourcesLoadError(null);
      })
      .catch((e) => setSourcesLoadError(formatApiError(e)));
  }, [canManageMembers, isPersonalMode, workspaceVersion]);

  function returnToDetails(refresh = false) {
    navigate(WORKSPACE_DETAILS_PATH, {
      replace: true,
      state: refresh ? { refresh: Date.now() } : undefined,
    });
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaveError(null);
    setSaving(true);
    try {
      await organizationsService.updateSettings({ name, timezone });
      toast('Organization updated successfully.', 'success');
      returnToDetails(true);
    } catch (err) {
      setSaveError(formatApiError(err, 'Update failed'));
      toast(formatApiError(err, 'Update failed'), 'error');
    } finally {
      setSaving(false);
    }
  }

  function toggleEnabledSource(sourceId: string) {
    const key = normalizeSourceId(sourceId);
    setEnabledSourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        setDefaultMemberSourceIds((defaults) => {
          const filtered = new Set(defaults);
          filtered.delete(key);
          return filtered;
        });
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function toggleDefaultMemberSource(sourceId: string) {
    const key = normalizeSourceId(sourceId);
    if (!enabledSourceIds.has(key)) return;
    setDefaultMemberSourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function saveWorkspaceSources(e: React.FormEvent) {
    e.preventDefault();
    setSourcesSaveError(null);
    setSavingSources(true);
    try {
      const enabledIds = enabledSources
        .filter((source) => enabledSourceIds.has(normalizeSourceId(source.id)))
        .map((source) => source.id);
      const defaultIds = workspaceEnabledPickerSources
        .filter((source) => defaultMemberSourceIds.has(normalizeSourceId(source.id)))
        .map((source) => source.id);
      await workspaceSourcesService.updateSettings({
        enabledSourceIds: enabledIds,
        defaultMemberSourceIds: defaultIds,
      });
      toast('Workspace sources updated.', 'success');
    } catch (err) {
      setSourcesSaveError(formatApiError(err, 'Update failed'));
      toast(formatApiError(err, 'Update failed'), 'error');
    } finally {
      setSavingSources(false);
    }
  }

  if (isPersonalMode) {
    return (
      <div>
        <PageHeader title="Organization settings" subtitle="Switch to an organization workspace first." />
        <section className="panel">
          <p className="muted">Organization settings require an active organization workspace.</p>
          <Link className="btn btn-sm" to="/workspace">
            Back to workspace
          </Link>
        </section>
      </div>
    );
  }

  if (loadError) return <div className="panel alert alert-error">{loadError}</div>;
  if (!org) return <div className="panel">Loading settings…</div>;

  return (
    <div>
      <PageHeader title="Organization settings" subtitle="Workspace name, sources, and defaults." />
      {canManageMembers ? (
        <form className="panel form-stack" onSubmit={save}>
          <label htmlFor="org-name">Organization name</label>
          <input id="org-name" value={name} onChange={(e) => setName(e.target.value)} required />
          <label htmlFor="org-timezone">Timezone</label>
          <input id="org-timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
          {saveError ? <div className="alert alert-error">{saveError}</div> : null}
          <div className="table-action-bar">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => returnToDetails()}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <section className="panel">
          <p className="muted">You can view workspace details but do not have permission to change settings.</p>
          <p>
            Organization: <strong>{org.name}</strong>
          </p>
          <p className="muted">Timezone: {org.timezone ?? 'UTC'}</p>
          <Link className="btn btn-sm" to={WORKSPACE_DETAILS_PATH}>
            Back to workspace
          </Link>
        </section>
      )}

      {canManageMembers ? (
        <form className="panel form-stack" onSubmit={saveWorkspaceSources}>
          <h2>Workspace sources</h2>
          <p className="muted">Sources enabled for this workspace.</p>
          {sourcesLoadError ? <div className="alert alert-error">{sourcesLoadError}</div> : null}
          <SourceAssignmentPicker
            sources={enabledSources}
            selected={enabledSourceIds}
            onToggle={toggleEnabledSource}
            onSelectAll={() =>
              setEnabledSourceIds(new Set(enabledSources.map((source) => normalizeSourceId(source.id))))
            }
            onClearAll={() => {
              setEnabledSourceIds(new Set());
              setDefaultMemberSourceIds(new Set());
            }}
            legend="Enabled sources for this workspace"
            hideLegend
            helperText={`${enabledSourceIds.size} of ${enabledSources.length} platform sources enabled`}
            summaryFormat="fraction"
            groupByCategory
          />

          <h3>Default member source access</h3>
          <p className="muted">Applied to new subscribers and viewers unless customized.</p>
          {workspaceEnabledPickerSources.length === 0 ? (
            <p className="muted">Enable workspace sources above before choosing defaults.</p>
          ) : (
            <SourceAssignmentPicker
              sources={workspaceEnabledPickerSources}
              selected={defaultMemberSourceIds}
              onToggle={toggleDefaultMemberSource}
              onSelectAll={() =>
                setDefaultMemberSourceIds(
                  new Set(workspaceEnabledPickerSources.map((source) => normalizeSourceId(source.id))),
                )
              }
              onClearAll={() => setDefaultMemberSourceIds(new Set())}
              legend="Default member source access"
              hideLegend
              helperText={`${defaultMemberSourceIds.size} of ${workspaceEnabledPickerSources.length} workspace sources`}
              summaryFormat="fraction"
              groupByCategory
            />
          )}

          {sourcesSaveError ? <div className="alert alert-error">{sourcesSaveError}</div> : null}
          <div className="table-action-bar">
            <button type="submit" className="btn btn-primary" disabled={savingSources}>
              {savingSources ? 'Saving…' : 'Save workspace sources'}
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
